/**
 * Costo de la campaña antes de confirmar: cuánto se estima, cuánto como máximo, si alcanza el
 * saldo y el tope, y el punto de equilibrio: qué porcentaje tiene que volver a comprar GRACIAS AL
 * MENSAJE para cubrir el costo. Al lado de esa cifra va siempre por qué no se va a poder medir
 * después: no hay grupo de control, y la medición cuenta también a los que habrían vuelto igual. Con
 * grupos de Activa, `TEXTO_EQUILIBRIO_SIN_CONTROL`; con un público de la base o el Público de Activa,
 * que se miden con la base de ventas en uso, `TEXTO_EQUILIBRIO_BASE` (con «comprar»: un contacto de la
 * base puede no haber comprado nunca).
 *
 * Las tarifas NUNCA se escriben en el front: todo sale del chequeo del backend (`costo`), que las
 * toma de la tabla de Meta vigente. Si `porMensajeUSD` no viene, se omite esa cifra en vez de
 * inventarla. Todo va en dólares, nunca en pesos (decisión de Alan del 26/10/2026).
 *
 * El margen por pedido lo escribe el operador, en dólares (no hay un dato confiable de margen en Activa,
 * SPEC-backend §19 C27) y se recuerda en el navegador POR COMERCIO: el margen de una hamburguesería
 * no es el de una óptica.
 */

import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet } from "lucide-react";
import type { Chequeo, CostoEstimado as Costo } from "../types";
import { usd, usdPorMensaje } from "../logic/formato";
import { puntoEquilibrio } from "../logic/chequeo";
import { diaEnZona, TZ_NEGOCIO } from "../logic/estadisticas";
import { CATEGORIA, TEXTO_EQUILIBRIO_BASE, TEXTO_EQUILIBRIO_SIN_CONTROL } from "../logic/textos";
import { activaApi } from "../../campana-nueva/api";

const RUTA_SALDO = "/activa/oficial/configuracion?seccion=saldo";

/**
 * Clave del margen recordado, por comercio. `margen-usd`: hasta el 26/10 el margen se guardaba en pesos cuando había
 * tipo de cambio, y leído como dólares un «5000» de antes daría un equilibrio absurdo. La clave nueva arranca vacía.
 */
export function claveMargen(brandId: string): string {
  return `deenex-activa:oficial:margen-usd:${brandId || "sin-marca"}`;
}

function leerMargen(k: string): string {
  try { return localStorage.getItem(k) || ""; } catch { return ""; }
}
function guardarMargen(k: string, v: string): void {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch { /* sin storage (modo privado): vale para esta pantalla */ }
}

/** "2026-07-01" como día local (con `new Date("2026-07-01")` en Argentina diría 30 de junio). */
function fechaDia(s?: string | null): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * De qué mes es el gasto contra el que se mide el tope: el chequeo lo mide contra el mes en que SALE
 * la campaña, en la zona del negocio (como `mesDe` del backend). Sin fecha, con una que no se lee o
 * dentro de este mes, «este mes»; programada para otro, ese mes («en octubre», con el año si cambia).
 */
function mesDelTope(programadaPara: string | null | undefined, ahora: Date): string {
  const d = programadaPara ? new Date(programadaPara) : null;
  if (!d || isNaN(d.getTime())) return "este mes";
  const mes = diaEnZona(d).slice(0, 7);
  const mesAhora = diaEnZona(ahora).slice(0, 7);
  if (mes === mesAhora) return "este mes";
  const nombre = new Intl.DateTimeFormat("es-AR", { timeZone: TZ_NEGOCIO, month: "long" }).format(d);
  return mes.slice(0, 4) === mesAhora.slice(0, 4) ? `en ${nombre}` : `en ${nombre} de ${mes.slice(0, 4)}`;
}

/** Porcentaje con un decimal, en castellano: 0.0161 → "1,6". */
function pct1(fraccion: number): string {
  return (fraccion * 100).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

interface Props {
  costo: Costo;
  saldo: Chequeo["saldo"];
  tope: Chequeo["tope"];
  /** El recargo de Deenex. No le llega a una persona de un cliente (el backend lo saca de la config): sin él, no se nombra. */
  recargoPct?: number | null;
  envio: number;
  /** La campaña le escribe a un público de la base: no hay ventas con qué medir el equilibrio. */
  audienciaBase?: boolean;
  /** La fecha (ISO) para la que se programa el envío, la misma con la que se pidió el chequeo. Sin ella, sale ahora. */
  programadaPara?: string | null;
}

export function CostoEstimado({ costo, saldo, tope, recargoPct, envio, audienciaBase = false, programadaPara = null }: Props) {
  const idMargen = useId();
  const clave = claveMargen(activaApi.marcaActual());
  const [margen, setMargen] = useState<string>(() => leerMargen(clave));
  useEffect(() => { setMargen(leerMargen(clave)); }, [clave]);

  const margenNum = Number(String(margen).replace(",", "."));
  const pe = margen ? puntoEquilibrio({ clienteEsperadoUSD: costo.clienteEsperadoUSD, envio, margenUSD: margenNum }) : null;
  const categoria = (CATEGORIA as Record<string, string>)[costo.categoria] || (costo.categoria === "SERVICIO" ? "Servicio" : String(costo.categoria || ""));

  const cambiarMargen = (v: string) => {
    const limpio = v.replace(/[^\d.,]/g, "").slice(0, 12);
    setMargen(limpio);
    guardarMargen(clave, limpio);
  };

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4" aria-label="Costo estimado">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0"><Wallet size={16} /></span>
        <div>
          <p className="text-2xl font-semibold tracking-tight text-slate-900">
            {usd(costo.clienteEsperadoUSD)} <span className="text-sm font-medium text-slate-500">estimado</span>
          </p>
          <p className="text-sm text-slate-500">hasta {usd(costo.clienteMaximoUSD)} si se entregan todos</p>
        </div>
      </div>

      {/* Sin «Twilio»: el dueño no sabe qué es (es por dónde sale el mensaje). Lo que cobra es «el envío». */}
      <p className="text-xs text-slate-500 leading-relaxed" data-testid="detalle-tarifa">
        {typeof costo.porMensajeUSD === "number" && Number.isFinite(costo.porMensajeUSD) ? `${usdPorMensaje(costo.porMensajeUSD)} por mensaje entregado ` : "Por mensaje entregado: "}
        ({categoria}, tarifa de Meta para Argentina vigente desde el {fechaDia(costo.tarifaVigencia)}, más el envío){typeof recargoPct === "number" && Number.isFinite(recargoPct) ? ` + ${recargoPct} % de servicio` : ""}.
        {" "}Los que fallan cuestan USD 0,001, salvo algunos fallos que no se cobran.
      </p>

      <div className="rounded-xl bg-slate-50 p-3 space-y-1 text-sm">
        <p className="text-slate-700">
          Saldo: <strong>{usd(saldo?.saldoUSD)}</strong>{" "}
          {saldo?.alcanza ? (
            <span className="font-semibold text-emerald-700">alcanza</span>
          ) : (
            <span className="font-semibold text-red-600">
              faltan {usd(saldo?.faltanteUSD)} · <Link to={RUTA_SALDO} className="underline">Cargar saldo</Link>
            </span>
          )}
        </p>
        {tope && (
          <p className={tope.alcanza ? "text-slate-600" : "text-red-600 font-medium"}>
            Gastado {mesDelTope(programadaPara, new Date())} {usd(tope.gastoMesUSD)} de {usd(tope.topeMensualUSD)}
            {!tope.alcanza && " · esta campaña pasa el tope"}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={idMargen} className="text-xs font-semibold text-slate-600">
          Ganancia promedio por pedido (en dólares)
        </label>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-sm text-slate-500">USD</span>
          <input
            id={idMargen}
            type="text"
            inputMode="decimal"
            value={margen}
            placeholder="5"
            onChange={(e) => cambiarMargen(e.target.value)}
            className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        {pe !== null && (
          <div className="mt-2 space-y-1" data-testid="punto-equilibrio">
            {/* Un contacto de la base puede no haber comprado nunca: no se le pide que «vuelva». */}
            <p className="text-sm text-slate-700">
              Para cubrir el costo, el <strong className="text-violet-700">{pct1(pe)} %</strong> de los que lo reciben tiene que {audienciaBase ? "comprar" : "volver a comprar"} gracias al mensaje.
            </p>
            {audienciaBase ? (
              <p className="text-xs text-slate-500 leading-relaxed" data-testid="punto-equilibrio-base">{TEXTO_EQUILIBRIO_BASE}</p>
            ) : (
              <p className="text-xs text-slate-500 leading-relaxed" data-testid="punto-equilibrio-sin-control">{TEXTO_EQUILIBRIO_SIN_CONTROL}</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default CostoEstimado;
